import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import type Uppy from "@uppy/core";
import type {
  FileUploadProgress,
  UseUppyUploadOptions,
} from "@/services/api/files";
import { updateProgressStatus } from "../../utils/uppyUtils";
import { createUppyEventHandlers } from "../../utils/uppyEventHandlers";

interface UseUppyUploadParams extends UseUppyUploadOptions {
  uppy: Uppy | null;
}

export function useUppyUpload({
  uppy,
  categoryId,
  attributeValues,
  tagIds,
  onUploadComplete,
  onUploadError,
  enabled = true,
}: UseUppyUploadParams) {
  const [uploadProgress, setUploadProgress] = useState<
    Map<string, FileUploadProgress>
  >(new Map());
  const [filesUpdateTrigger, setFilesUpdateTrigger] = useState(0);
  const fileIdMapRef = useRef<Map<string, string>>(new Map());
  const isUploadingRef = useRef(false);
  const uploadProgressRef = useRef(uploadProgress);
  const refs = useRef({
    categoryId,
    attributeValues,
    tagIds,
    onUploadComplete,
    onUploadError,
  });

  useEffect(() => {
    uploadProgressRef.current = uploadProgress;
    refs.current = {
      categoryId,
      attributeValues,
      tagIds,
      onUploadComplete,
      onUploadError,
    };
  }, [
    uploadProgress,
    categoryId,
    attributeValues,
    tagIds,
    onUploadComplete,
    onUploadError,
  ]);

  useEffect(() => {
    if (!enabled || !uppy) return;

    const handlers = createUppyEventHandlers({
      uppy,
      fileIdMapRef,
      isUploadingRef,
      uploadProgressRef,
      setUploadProgress,
      setFilesUpdateTrigger,
      refs,
    });

    const eventHandlers = [
      ["upload", handlers.handleUpload],
      ["upload-success", handlers.handleUploadSuccess],
      ["file-added", handlers.handleFileAdded],
      ["file-removed", handlers.handleFileRemoved],
      ["upload-progress", handlers.handleUploadProgress],
      ["complete", handlers.handleComplete],
      ["upload-error", handlers.handleUploadError],
    ] as const;

    eventHandlers.forEach(([event, handler]) => uppy.on(event, handler));
    const fileIdMap = fileIdMapRef.current;
    return () => {
      eventHandlers.forEach(([event, handler]) => uppy.off(event, handler));
      fileIdMap.clear();
    };
  }, [enabled, uppy]);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!uppy) return;
      files.forEach((f) =>
        uppy.addFile({ name: f.name, type: f.type, data: f, source: "Local" })
      );
    },
    [uppy]
  );

  const removeFile = useCallback(
    (fileId: string) => uppy?.removeFile(fileId),
    [uppy]
  );
  const upload = useCallback(() => uppy?.upload(), [uppy]);
  const cancelAll = useCallback(() => {
    uppy?.cancelAll();
    isUploadingRef.current = false;
  }, [uppy]);

  const files = useMemo(() => {
    if (!uppy) return [];
    return uppy
      .getFiles()
      .map((f) =>
        Object.assign(
          (f.data as File) || new File([], f.name || f.id, { type: f.type }),
          { uppyId: f.id }
        )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy, filesUpdateTrigger]);

  const retryFile = useCallback(
    (fileId: string) => {
      if (!uppy) return;
      const file = uppy.getFile(fileId);
      if (!file) return;
      fileIdMapRef.current.delete(fileId);
      uppy.retryUpload(fileId);
      setUploadProgress((prev) =>
        updateProgressStatus(prev, fileId, {
          status: "pending",
          progress: 0,
          error: undefined,
        })
      );
      if (!isUploadingRef.current) {
        isUploadingRef.current = true;
        uppy.upload();
      }
    },
    [uppy]
  );

  const reset = useCallback(() => {
    if (!uppy) return;
    uppy.cancelAll();
    uppy.getFiles().forEach((f) => {
      if (f.meta) {
        delete f.meta.uploadUrl;
        delete f.meta.fileId;
      }

      if (f.tus) {
        delete f.tus.uploadUrl;
      }
      uppy.removeFile(f.id);
    });

    try {
      [localStorage, sessionStorage].forEach((storage) => {
        Object.keys(storage).forEach((key) => {
          if (key.startsWith("tus::")) {
            storage.removeItem(key);
          }
        });
      });
    } catch (error) {
      console.warn("Failed to clear TUS storage:", error);
    }
    setUploadProgress(new Map());
    setFilesUpdateTrigger((p) => p + 1);
    fileIdMapRef.current.clear();
    isUploadingRef.current = false;
  }, [uppy]);

  const uploadProgressArray = useMemo(
    () => Array.from(uploadProgress.values()),
    [uploadProgress]
  );

  return {
    uppy,
    files,
    uploadProgress: uploadProgressArray,
    addFiles,
    removeFile,
    upload,
    retryFile,
    cancelAll,
    reset,
  };
}
